import { motion, useScroll, useMotionValueEvent } from 'framer-motion'
import { useState } from 'react'
import vectorIcon from '../../../assets/images/icons/vector.svg'

export type BlogDetailArticle = {
  introStrip: string
  introMeta: string
  introHeading: string
  introSubline: string
  paragraphsAfterIntro: string[]
  inlineImages: string[]
  midHeading: string
  midParagraph: string
  quoteIcon: string
  quoteParagraph: string
  quoteAuthor: string
  closingParagraphs: string[]
}

type BlogDetailsProps = {
  article: BlogDetailArticle
}

const cardShell = 'overflow-hidden rounded-2xl shadow-xl shadow-black/40'
const cardBg = { backgroundColor: '#110B2D' } as const
const orangeGradient = 'linear-gradient(92.21deg,#EA892A 0%,#F0578B 100%)'

const BlogDetails = ({ article }: BlogDetailsProps) => {
  const {
    paragraphsAfterIntro,
    inlineImages,
    midHeading,
    midParagraph,
    quoteParagraph,
    closingParagraphs,
  } = article

  // 🔥 scroll direction
  const { scrollY } = useScroll()
  const [dir, setDir] = useState<'up' | 'down'>('down')

  useMotionValueEvent(scrollY, 'change', (latest) => {
    const prev = scrollY.getPrevious()
    if (latest > (prev ?? 0)) setDir('down')
    else setDir('up')
  })

  return (
    <div className="relative mx-auto w-[75%] pb-10 md:pb-14">

      {/* 🔥 HERO OVERLAP */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: false, amount: 0.4 }}
        transition={{ duration: 0.7 }}
        className="md:max-w-[60%] lg:max-w-[60%] xl:max-w-[52%] 2xl:max-w-[40%] relative z-30 mx-auto -mt-[clamp(5rem,18vw,8rem)] md:-mt-[clamp(6rem,14vw,3rem)]"
      >
        <article className={cardShell} style={cardBg}>
           <div className="flex flex-col items-center px-5 pb-8 pt-6 text-center md:px-8 md:pb-10 md:pt-8"> 
            <h1 className="mb-7 2xl:mt-7 rounded-2xl bg-purple px-8 py-3 font-gilroy-medium text-2xl font-semibold uppercase text-white md:text-xl lg:text-xl"> it solutions </h1> 
            <div className="mb-2 flex items-center justify-center gap-3 text-sm text-gray-200 md:text-xl">
               <h6 className="font-gilroy-medium text-gray-200/50">By Techto</h6> 
               <span className="text-gray-100 mb-2 ">-</span> 
               <span className="font-gilroy-medium text-gray-200/50">February 3, 2025</span>
                </div>
                 <h2 className="mb-3 2xl:mb-6 font-rajdhani text-2xl font-semibold text-white md:text-3xl lg:text-3xl xl:text-4xl"> Adopt Zero Trust across the network </h2>
                  </div>
                   </article>
      </motion.div>

      {/* 🔥 PARAGRAPHS */}
      <div className="mx-auto mt-8 space-y-5 text-center font-gilroy-medium text-sm leading-relaxed text-gray-200/50 md:mt-10 md:text-base lg:text-base xl:text-lg 2xl:text-3xl">
        {paragraphsAfterIntro.map((text, i) => (
          <motion.p
            key={i}
            initial={{
              opacity: 0,
              y: dir === 'down' ? 50 : -50,
            }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
          >
            {text}
          </motion.p>
        ))}
      </div>

      {/* 🔥 IMAGES */}
      <div className="mx-auto lg:max-w-[800px] xl:max-w-[900px] 2xl:max-w-[1400px] mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-12 md:gap-6 2xl:gap-10">
        <motion.img
          src={inlineImages[0]}
          alt=""
          className="h-[200px] w-full rounded-3xl object-cover md:h-[240px] lg:h-[300px] xl:h-[350px] 2xl:h-[470px]"
          initial={{ x: -110, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: false }}
          transition={{ duration: 0.5 }}
        />
        <motion.img
          src={inlineImages[1]}
          alt=""
          className="h-[200px] w-full rounded-3xl object-cover md:h-[240px] lg:h-[300px] xl:h-[350px] 2xl:h-[470px]"
          initial={{ x: 110, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          viewport={{ once: false }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* 🔥 MID TEXT */}
      <div className="mx-auto mt-10 text-center md:mt-12">
        <h3 className="mb-4 font-rajdhani text-xl font-semibold text-white md:text-base lg:text-2xl xl:text-3xl 2xl:text-5xl">
          {midHeading}
        </h3>
        <p className="mx-auto font-gilroy-medium text-sm leading-relaxed text-gray-200/50 md:text-base lg:text-lg xl:text-xl 2xl:text-4xl">
          {midParagraph}
        </p>
      </div>

      {/* 🔥 QUOTE CARD */}
      <motion.article
        className={`${cardShell} mx-auto mt-12 md:mt-14`}
        style={cardBg}
        initial={{
          y: dir === 'down' ? 80 : -80,
          opacity: 0,
        }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: false }}
        transition={{ duration: 0.5 }}
      >
        <div className="relative p-5 md:p-8">
          <div className="flex items-start gap-5 md:gap-7">

            {/* 🔁 ICON */}
            <motion.span
              aria-hidden
              className="mt-1 h-10 w-10 shrink-0 md:h-6 md:w-6 lg:h-7 lg:w-7 xl:h-8 xl:w-8 2xl:h-14 2xl:w-14"
              style={{
                background: orangeGradient,
                WebkitMaskImage: `url(${vectorIcon})`,
                maskImage: `url(${vectorIcon})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />

            <div className="flex-1">
              <p className="font-gilroy-medium text-sm leading-relaxed text-gray-200/70 md:text-base lg:text-base xl:text-lg 2xl:text-3xl">
                {quoteParagraph}
              </p>

              {/* 🔁 AUTHOR */}
              <motion.p
                className="mt-5 bg-clip-text text-left font-rajdhani text-base font-semibold text-transparent md:text-base lg:text-xl xl:text-2xl 2xl:text-4xl"
                style={{ backgroundImage: orangeGradient }}
                animate={{ x: [0, 6, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                Adam Peterson
              </motion.p>
            </div>
          </div>
        </div>
      </motion.article>

      {/* 🔥 CLOSING */}
      <div className="mx-auto mt-10 space-y-5 text-center font-gilroy-medium text-sm leading-relaxed text-gray-200/50 md:mt-12 md:text-base lg:text-base xl:text-lg 2xl:text-3xl">
        {closingParagraphs.map((text, i) => (
          <motion.p
            key={i}
            initial={{
              opacity: 0,
              y: dir === 'down' ? 50 : -50,
            }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
          >
            {text}
          </motion.p>
        ))}
      </div>
    </div>
  )
}

export default BlogDetails